import { useState, useEffect, useMemo } from 'react';

/**
 * Theme color palettes for Recharts and inline JS styling.
 *
 * Each palette provides resolved hex values (NOT CSS variable strings)
 * because Recharts renders to SVG and canvas where CSS variables can be
 * unreliable. Every palette mirrors the CSS variables defined in
 * themeVariations.css so that charts stay visually consistent with the
 * rest of the UI.
 *
 * Structure per theme:
 *   chart    – ordered array of 5 colors for multi-series charts
 *   severity – critical / high / medium / low / info
 *   risk     – critical / atRisk / caution / healthy (risk-level gauge)
 *   accent   – primary / secondary / tertiary
 *   bg       – primary / secondary / card
 *   text     – primary / secondary / muted
 *   border   – dark / medium
 *   brand    – primary color
 */

const THEME_PALETTES = {
  // ── Dark themes ──────────────────────────────────────────

  'deep-ocean': {
    chart:    ['#00D4AA', '#00BFA5', '#00E5FF', '#40C4FF', '#80DEEA'],
    severity: { critical: '#FF5252', high: '#FF8A65', medium: '#FFD54F', low: '#69F0AE', info: '#40C4FF' },
    risk:     { critical: '#FF5252', atRisk: '#FF8A65', caution: '#FFD54F', healthy: '#69F0AE' },
    accent:   { primary: '#00D4AA', secondary: '#00BFA5', tertiary: '#00E5FF' },
    bg:       { primary: '#0a1628', secondary: '#0d2137', card: '#112a45' },
    text:     { primary: '#e8f4f8', secondary: '#8cb4cc', muted: '#5a8ea8' },
    border:   { dark: '#1a3a5c', medium: '#245a80' },
    brand:    '#00D4AA',
  },

  'bioluminescent': {
    chart:    ['#00FFD4', '#00E5FF', '#18FFFF', '#64FFDA', '#84FFFF'],
    severity: { critical: '#FF6B6B', high: '#FFA06B', medium: '#FFD93D', low: '#6BFFB8', info: '#6BE5FF' },
    risk:     { critical: '#FF6B6B', atRisk: '#FFA06B', caution: '#FFD93D', healthy: '#6BFFB8' },
    accent:   { primary: '#00FFD4', secondary: '#00E5FF', tertiary: '#18FFFF' },
    bg:       { primary: '#050A14', secondary: '#0B1426', card: '#0F1B33' },
    text:     { primary: '#E0F7FA', secondary: '#80CBC4', muted: '#4DB6AC' },
    border:   { dark: '#1a2d4d', medium: '#234060' },
    brand:    '#00FFD4',
  },

  'abyssal': {
    chart:    ['#4ECDC4', '#45B7AA', '#2EC4B6', '#40C4FF', '#80DEEA'],
    severity: { critical: '#FF5252', high: '#FF8A65', medium: '#FFD54F', low: '#69F0AE', info: '#40C4FF' },
    risk:     { critical: '#FF5252', atRisk: '#FF8A65', caution: '#FFD54F', healthy: '#69F0AE' },
    accent:   { primary: '#4ECDC4', secondary: '#45B7AA', tertiary: '#2EC4B6' },
    bg:       { primary: '#000000', secondary: '#0A0A0A', card: '#111111' },
    text:     { primary: '#E0E0E0', secondary: '#999999', muted: '#666666' },
    border:   { dark: '#1E1E1E', medium: '#2A2A2A' },
    brand:    '#4ECDC4',
  },

  'warm': {
    chart:    ['#e63946', '#4ecdc4', '#f4a261', '#FF6B35', '#FFB800'],
    severity: { critical: '#FF0000', high: '#FF6B35', medium: '#FFB800', low: '#39FF14', info: '#00D9FF' },
    risk:     { critical: '#FF0000', atRisk: '#FF6B35', caution: '#FFB800', healthy: '#00ff64' },
    accent:   { primary: '#e63946', secondary: '#4ecdc4', tertiary: '#f4a261' },
    bg:       { primary: '#1a1916', secondary: '#232019', card: '#2d2a24' },
    text:     { primary: '#f5f5f0', secondary: '#b8b5ae', muted: '#8a8680' },
    border:   { dark: '#3d3933', medium: '#4a4640' },
    brand:    '#e63946',
  },

  'cool': {
    chart:    ['#ff4757', '#00d9ff', '#5f27cd', '#FF6B35', '#FFB800'],
    severity: { critical: '#FF0000', high: '#FF6B35', medium: '#FFB800', low: '#39FF14', info: '#00D9FF' },
    risk:     { critical: '#FF0000', atRisk: '#FF6B35', caution: '#FFB800', healthy: '#00ff64' },
    accent:   { primary: '#ff4757', secondary: '#00d9ff', tertiary: '#5f27cd' },
    bg:       { primary: '#0f1419', secondary: '#1a2027', card: '#242d38' },
    text:     { primary: '#f7f9fc', secondary: '#a8b3c4', muted: '#7a8a9c' },
    border:   { dark: '#3a4654', medium: '#4a5a6a' },
    brand:    '#ff4757',
  },

  'neutral': {
    chart:    ['#dc3545', '#17a2b8', '#6c757d', '#FF6B35', '#FFB800'],
    severity: { critical: '#FF0000', high: '#FF6B35', medium: '#FFB800', low: '#39FF14', info: '#00D9FF' },
    risk:     { critical: '#FF0000', atRisk: '#FF6B35', caution: '#FFB800', healthy: '#00ff64' },
    accent:   { primary: '#dc3545', secondary: '#17a2b8', tertiary: '#6c757d' },
    bg:       { primary: '#121212', secondary: '#1e1e1e', card: '#252525' },
    text:     { primary: '#ffffff', secondary: '#aaaaaa', muted: '#777777' },
    border:   { dark: '#333333', medium: '#444444' },
    brand:    '#dc3545',
  },

  'soft': {
    chart:    ['#e06c75', '#61afef', '#c678dd', '#FF6B35', '#FFB800'],
    severity: { critical: '#FF0000', high: '#FF6B35', medium: '#FFB800', low: '#39FF14', info: '#00D9FF' },
    risk:     { critical: '#FF0000', atRisk: '#FF6B35', caution: '#FFB800', healthy: '#00ff64' },
    accent:   { primary: '#e06c75', secondary: '#61afef', tertiary: '#c678dd' },
    bg:       { primary: '#181a1f', secondary: '#1f2228', card: '#282c34' },
    text:     { primary: '#e6e8eb', secondary: '#9da5b4', muted: '#6b7280' },
    border:   { dark: '#3a3f4a', medium: '#4a505c' },
    brand:    '#e06c75',
  },

  // ── Light themes ──────────────────────────────────────────

  'coastal-light': {
    chart:    ['#0891B2', '#0D9488', '#0284C7', '#0369A1', '#155E75'],
    severity: { critical: '#DC2626', high: '#EA580C', medium: '#CA8A04', low: '#16A34A', info: '#0284C7' },
    risk:     { critical: '#DC2626', atRisk: '#EA580C', caution: '#CA8A04', healthy: '#16A34A' },
    accent:   { primary: '#0891B2', secondary: '#0D9488', tertiary: '#06B6D4' },
    bg:       { primary: '#F5F7FA', secondary: '#FFFFFF', card: '#FFFFFF' },
    text:     { primary: '#1E293B', secondary: '#475569', muted: '#94A3B8' },
    border:   { dark: '#E2E8F0', medium: '#CBD5E1' },
    brand:    '#0891B2',
  },

  'arctic-frost': {
    chart:    ['#0077B6', '#0096C7', '#00B4D8', '#0369A1', '#155E75'],
    severity: { critical: '#DC2626', high: '#EA580C', medium: '#CA8A04', low: '#16A34A', info: '#0284C7' },
    risk:     { critical: '#DC2626', atRisk: '#EA580C', caution: '#CA8A04', healthy: '#16A34A' },
    accent:   { primary: '#0077B6', secondary: '#0096C7', tertiary: '#00B4D8' },
    bg:       { primary: '#F0F4F8', secondary: '#F8FAFC', card: '#FFFFFF' },
    text:     { primary: '#1A2B3C', secondary: '#455A64', muted: '#78909C' },
    border:   { dark: '#D1DAE6', medium: '#B0BEC5' },
    brand:    '#0077B6',
  },

  'sunrise-reef': {
    chart:    ['#E07A5F', '#3D405B', '#F2CC8F', '#C1440E', '#81B29A'],
    severity: { critical: '#DC2626', high: '#EA580C', medium: '#CA8A04', low: '#16A34A', info: '#0284C7' },
    risk:     { critical: '#DC2626', atRisk: '#EA580C', caution: '#CA8A04', healthy: '#16A34A' },
    accent:   { primary: '#E07A5F', secondary: '#3D405B', tertiary: '#F2CC8F' },
    bg:       { primary: '#FDF8F3', secondary: '#FFFFFF', card: '#FFFFFF' },
    text:     { primary: '#2D1F14', secondary: '#5D4E42', muted: '#9A897A' },
    border:   { dark: '#E8DDD0', medium: '#D4C4B0' },
    brand:    '#E07A5F',
  },
};

// Fallback to arctic-frost (the default theme)
const DEFAULT_THEME = 'arctic-frost';

/**
 * Read the current theme id from the DOM.
 */
function getCurrentTheme() {
  if (typeof document === 'undefined') return DEFAULT_THEME;
  return document.documentElement.getAttribute('data-theme') || DEFAULT_THEME;
}

/**
 * useThemeColors – React hook that returns the resolved color palette
 * for the active theme.  Automatically re-renders when the user switches
 * themes via Settings (uses a MutationObserver on data-theme).
 *
 * @returns {object} The full palette for the current theme (see structure above).
 */
export function useThemeColors() {
  const [theme, setTheme] = useState(getCurrentTheme);

  useEffect(() => {
    // Re-read immediately in case the attribute already changed before mount
    setTheme(getCurrentTheme());

    // Watch for data-theme attribute changes on <html>
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-theme'
        ) {
          setTheme(getCurrentTheme());
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  return useMemo(() => THEME_PALETTES[theme] || THEME_PALETTES[DEFAULT_THEME], [theme]);
}

/**
 * getThemeColors – non-hook utility for use outside React components
 * (e.g. inside data-transform callbacks or event handlers).
 * Returns a snapshot – does NOT auto-update on theme switch.
 *
 * @param {string} [themeId] – optional override; defaults to current DOM theme.
 * @returns {object} The full palette.
 */
export function getThemeColors(themeId) {
  const id = themeId || getCurrentTheme();
  return THEME_PALETTES[id] || THEME_PALETTES[DEFAULT_THEME];
}

export default useThemeColors;
